import { pushEvent } from "@/helpers/analytics.js";

const MAX_TEXT = 300;
const MAX_PER_SESSION = 25;
const DEDUPE_MS = 8000;
const CLIENT_ERROR_PATH = "/internal/client-errors";

let sessionCount = 0;
/** @type {Map<string, number>} */
const recentFingerprints = new Map();

export function redactClientErrorText(value, max = MAX_TEXT) {
    if (value == null) {
        return "";
    }
    return String(value)
        .replace(/[^\s]+@[^\s]+/g, "[email]")
        .replace(/\+?\d[\d\s\-()]{7,}\d/g, "[phone]")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, max);
}

export function buildClientErrorPayload(input = {}) {
    const pagePath =
        typeof window !== "undefined"
            ? window.location?.pathname || ""
            : String(input.page_path || "");
    const href =
        typeof window !== "undefined" ? window.location?.href || "" : String(input.page_location || "");
    return {
        source: redactClientErrorText(input.source || "unknown", 40),
        step: redactClientErrorText(input.step || "", 40),
        reason: redactClientErrorText(input.reason || "", 60),
        description: redactClientErrorText(input.description || input.message || "", MAX_TEXT),
        fatal: input.fatal === true,
        severity: input.severity === "warning" ? "warning" : "error",
        page_path: redactClientErrorText(pagePath, 180),
        page_location: redactClientErrorText(href, 240),
        ts: new Date().toISOString(),
    };
}

function fingerprint(payload) {
    return [payload.page_path, payload.reason, payload.description, payload.step].join("|");
}

function shouldSkip(payload) {
    if (sessionCount >= MAX_PER_SESSION) {
        return true;
    }
    const key = fingerprint(payload);
    const now = Date.now();
    const prev = recentFingerprints.get(key);
    if (prev && now - prev < DEDUPE_MS) {
        return true;
    }
    recentFingerprints.set(key, now);
    if (recentFingerprints.size > 80) {
        for (const [k, t] of recentFingerprints) {
            if (now - t > DEDUPE_MS) {
                recentFingerprints.delete(k);
            }
        }
    }
    sessionCount += 1;
    return false;
}

function postToServer(payload) {
    if (typeof fetch !== "function") {
        return;
    }
    try {
        fetch(CLIENT_ERROR_PATH, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify(payload),
            keepalive: true,
        }).catch(() => {});
    } catch {
        /* ignore */
    }
}

/**
 * GA4 (`exception` + optional `checkout_error`) and a copy on the SSR host.
 * Never include phones, emails, tokens, or cart contents.
 */
export function reportClientError(input = {}) {
    if (typeof window === "undefined") {
        return null;
    }
    const payload = buildClientErrorPayload(input);
    if (!payload.description && !payload.reason) {
        return null;
    }
    if (shouldSkip(payload)) {
        return payload;
    }

    pushEvent("exception", {
        description: payload.description || payload.reason || payload.source,
        fatal: payload.fatal,
        error_source: payload.source,
        error_step: payload.step || undefined,
        error_reason: payload.reason || undefined,
        error_severity: payload.severity,
        page_path: payload.page_path,
    });

    const checkoutLike =
        input.checkout === true ||
        payload.source === "checkout" ||
        payload.page_path.includes("cart-confirmation") ||
        payload.page_path.includes("fast-cart");
    if (checkoutLike) {
        pushEvent("checkout_error", {
            step: payload.step || "unknown",
            reason: payload.reason || "unknown",
            description: payload.description,
            page_path: payload.page_path,
        });
    }

    postToServer(payload);
    return payload;
}

export function initClientErrorTracking() {
    if (typeof window === "undefined" || window.__tazejErrorTracking) {
        return;
    }
    window.__tazejErrorTracking = true;

    window.addEventListener("error", (event) => {
        const msg = event?.message || event?.error?.message;
        if (!msg || String(msg).includes("ResizeObserver") || String(msg).includes("Script error")) {
            return;
        }
        reportClientError({
            source: "window",
            reason: "uncaught",
            description: msg,
            fatal: true,
        });
    });

    window.addEventListener("unhandledrejection", (event) => {
        const reason = event?.reason;
        const msg = reason?.message || (typeof reason === "string" ? reason : "unhandledrejection");
        reportClientError({
            source: "window",
            reason: "unhandledrejection",
            description: msg,
            fatal: false,
        });
    });
}
